/**
 * Strategy-level feature chips — color/border classes for the small chips
 * shown under each strategy name on the Baskets page (MSLC / ORB / TP / SL).
 *
 * Keyed by semantic feature name so each chip routes to its canonical
 * `CHIP_DENSE` hue:
 *   mslc → CHIP_DENSE.MSLC        (sky    — Move SL to Cost)
 *   orb  → CHIP_DENSE.ORB         (amber  — Opening Range Breakout)
 *   tp   → CHIP_DENSE.RE_EXEC_TP  (emerald — Take-profit family)
 *   sl   → CHIP_DENSE.MANUAL_RETRY (orange — warning hue; no canonical SL entry)
 *
 * Cross-app consistency: this matches the ORB chip's amber rendering in
 * ScheduleTable, ProScheduleTable, StrategyPositionsModalV2, and EntrySourceBadge.
 *
 * Border colors stay per-tone because this surface uses a thin border in
 * addition to the chip's bg+text (not the case for dense status chips).
 *
 * Lives under `utils/` rather than colocated with `StrategyRowView.tsx` so
 * tests can import it without dragging the component's transitive service
 * dependencies through Jest. The component imports the constant + type from
 * here.
 */
import { CHIP_DENSE } from './chipColors';

export const FEATURE_TONES = {
  mslc: `${CHIP_DENSE.MSLC} border-sky-200 dark:border-sky-800/50`,
  orb:  `${CHIP_DENSE.ORB} border-amber-200 dark:border-amber-800/50`,
  tp:   `${CHIP_DENSE.RE_EXEC_TP} border-emerald-200 dark:border-emerald-800/50`,
  sl:   `${CHIP_DENSE.MANUAL_RETRY} border-orange-200 dark:border-orange-800/50`,
  // ul → CHIP_DENSE.UL (yellow — matches the UL_MONITOR_PENDING status hue
  // so the "spot-driven" visual anchor stays consistent card → position).
  ul:   `${CHIP_DENSE.UL} border-yellow-200 dark:border-yellow-800/50`,
} as const;

export type FeatureChipTone = keyof typeof FEATURE_TONES;
